/**
 * API Helper - Global 401 handling for Edge Function calls
 * Automatically logs out user when token is invalid/expired
 * Includes Sentry performance tracing for API calls
 */

import * as Sentry from '@sentry/react';
import { supabase } from './supabase';
import { auth$ } from '../store/auth$';
import { logger } from './logger';

/**
 * Get the Supabase client for direct DB operations
 */
export const getSupabase = () => supabase;

/**
 * Force logout - clears local state and signs out
 * Called when we detect an invalid/expired token
 */
export const forceLogout = async () => {
  console.log('[Mellow API] Token invalid/expired, forcing logout...');
  
  // Clear local state immediately
  auth$.user.set(null);
  auth$.session.set(null);
  auth$.isAuthenticated.set(false);
  
  // Try to sign out from Supabase (may fail if session already gone)
  try {
    await supabase.auth.signOut();
  } catch (err) {
    // Ignore - session may already be invalid
  }
};

/**
 * Get current access token
 * Returns null if not authenticated
 */
export const getAccessToken = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return session?.access_token || null;
};

/**
 * Fetch wrapper for Edge Function calls
 * Handles 401 errors with retry logic before forcing logout
 * Includes Sentry performance tracing
 *
 * @param {string} functionName - Edge Function name (e.g., 'mellow-ai-chat')
 * @param {object} body - Request body (will be JSON stringified)
 * @param {object} options - Additional fetch options
 * @returns {Promise<Response>} - Fetch response
 * @throws {Error} - If not authenticated or request fails
 */
export const fetchEdgeFunction = async (functionName, body, options = {}) => {
  // Wrap in Sentry span for performance tracking
  return await Sentry.startSpan(
    {
      op: 'http.client',
      name: `POST /functions/v1/${functionName}`,
      attributes: {
        'http.method': 'POST',
        'http.url': functionName,
      },
    },
    async (span) => {
      try {
        const token = await getAccessToken();

        if (!token) {
          throw new Error('Not authenticated');
        }

        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/${functionName}`;

        const response = await fetch(url, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            ...options.headers,
          },
          body: JSON.stringify(body),
          signal: options.signal,
          ...options,
        });

        // Add response status to span
        span.setAttribute('http.status_code', response.status);

        // Handle 401 Unauthorized with retry logic
        if (response.status === 401) {
          logger.warn('Got 401, attempting to refresh session', { functionName });

          // Try to refresh the session
          const { data: { session }, error } = await supabase.auth.refreshSession();

          if (error || !session) {
            // Refresh failed - session is truly invalid
            logger.error('Session refresh failed, logging out', { functionName, error: error?.message });
            await forceLogout();
            throw new Error('Session expired. Please log in again.');
          }

          // Retry the request with the new token
          logger.info('Session refreshed, retrying request', { functionName });
          const retryResponse = await fetch(url, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
              ...options.headers,
            },
            body: JSON.stringify(body),
            signal: options.signal,
            ...options,
          });

          // Update span with retry status
          span.setAttribute('http.status_code', retryResponse.status);
          span.setAttribute('http.retry', true);

          // If retry also fails with 401, then force logout
          if (retryResponse.status === 401) {
            logger.error('Retry also got 401, logging out', { functionName });
            await forceLogout();
            throw new Error('Session expired. Please log in again.');
          }

          return retryResponse;
        }

        return response;
      } catch (error) {
        // Mark span as error
        span.setStatus({ code: 2, message: error.message }); // 2 = ERROR
        throw error;
      }
    }
  );
};

/**
 * Fetch Edge Function and parse JSON response
 * Handles 401 and throws on non-OK responses
 * 
 * @param {string} functionName - Edge Function name
 * @param {object} body - Request body
 * @returns {Promise<object>} - Parsed JSON response
 */
export const callEdgeFunction = async (functionName, body) => {
  const response = await fetchEdgeFunction(functionName, body);
  
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Request failed: ${response.status}`);
  }
  
  return response.json();
};

